import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useDispatch, useSelector } from 'react-redux';
import { login, getUser } from "Features/api/api";
import Modal from "SharedFns/Modal";
import PasswordResetForm from "./PasswordResetForm";
import Spinner from "SharedFns/SpinnerElement";

const SignIn = ({ isOpen, onClose, onRegisterClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(state => state.signIn?.loading);

  const initialFormData = { email: "", password: "" };
  const [logInData, setLoginData] = useState(initialFormData);
  const [ISPasswordTouched, setIsPasswordTouched] = useState(false);
  const [ISEmailTouched, setISEmailTouched] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const isSubmitDisabled = (!ISEmailTouched || !ISPasswordTouched);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(login(logInData));
      resetFormValues();
      await dispatch(getUser());
      navigate("/dashboard");
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginData({ ...logInData, [name]: value });

    if (value === "") {
      if (name === "email") setISEmailTouched(false);
      if (name === "password") setIsPasswordTouched(false);
    } else {
      if (name === "email") setISEmailTouched(true);
      if (name === "password") setIsPasswordTouched(true);
    }
  };

  // const handleRegisterClick = (e) => {
  //   e.preventDefault();
  //   onRegisterClick();
  //   onChange(true); // Switches to SignUp
  // };

  const resetFormValues = () => {
    setLoginData(initialFormData);
    setISEmailTouched(false);
    setIsPasswordTouched(false);
  };
  if (!isOpen) return null;

return (
  <ModalBackground>
    {isLoading && <Spinner />}

    <Modal isOpen={isResetModalOpen} onClose={() => setIsResetModalOpen(false)  } title="👤 Reset password">
      <PasswordResetForm />
    </Modal>

    <ModalContainer>
      <Card>
        <HeaderRow>
          <Title>Sign In</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </HeaderRow>

        <SubText>
          Sign in to continue. Remember, your password is yours, do not share it with anyone.
        </SubText>
        <SubText>
          If this is your first time here, register with your Eye20 Group details.
        </SubText>

        <form onSubmit={handleSubmit}>
          <FormRow>
            <label htmlFor="email">Email address or phone number</label>
            <input
              name="email"
              id="email"
              type="email"
              value={logInData.email}
              onChange={handleChange}
              placeholder="Enter email or phone number"
              required
            />
            {ISEmailTouched && (
              <ErrorText>Your email or mobile number is required</ErrorText>
            )}
          </FormRow>

          <FormRow>
            <label htmlFor="password">Enter Password</label>
            <input
              name="password"
              id="password"
              type="password"
              value={logInData.password}
              onChange={handleChange}
              autoComplete="current-password"
              placeholder="Enter your password"
              required
            />
            {ISPasswordTouched && (
              <ErrorText>Please enter your password</ErrorText>
            )}
          </FormRow>

          <ForgotPasswordBtn type="button" onClick={() => setIsResetModalOpen(true)}>
            Forgot your password?
          </ForgotPasswordBtn>

          <ButtonRow>
            <SignInBtn disabled={isSubmitDisabled} type="submit">
              Sign In
            </SignInBtn>
          </ButtonRow>
        </form>
      </Card>
    </ModalContainer>
  </ModalBackground>
);

};

export default SignIn;

const ModalBackground = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.65);
  z-index: 1099;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 4rem 1rem 2rem; /* ensures spacing from top/navbar */
  overflow-y: auto;
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  background: #fefefe;
  padding: 2rem 1.5rem;
  border-radius: 14px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.3s ease-out;

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    border-radius: 10px;
  }

  @keyframes fadeIn {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  color: #003366;
`;

const CloseBtn = styled.button`
  font-size: 1.5rem;
  background: transparent;
  border: none;
  color: #444;
  cursor: pointer;
`;

const SubText = styled.p`
  font-size: 0.9rem;
  margin-top: 0.5rem;
  color: #555;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1.25rem;

  label {
    font-size: 0.8rem;
    margin-bottom: 6px;
    color: #333;
  }

  input {
    border: none;
    border-bottom: 1px solid #ccc;
    padding: 8px 4px;
    font-size: 0.95rem;
    outline: none;
    background: transparent;

    &:focus {
      border-bottom: 1.5px solid #0077cc;
    }
  }
`;

const ErrorText = styled.div`
  color: red;
  font-size: 0.75rem;
  margin-top: 4px;
`;

const ForgotPasswordBtn = styled.button`
  color: #0077cc;
  font-size: 0.85rem;
  background: none;
  border: none;
  margin-top: 0.75rem;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonRow = styled.div`
  margin-top: 2rem;
`;

const SignInBtn = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background-color: ${props => props.disabled ? "#ccc" : "#3498db"};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 1rem;
  cursor: ${props => props.disabled ? "not-allowed" : "pointer"};
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${props => props.disabled ? "#ccc" : "#2980b9"};
  }
`;
